import { useQuery } from '@apollo/client';
import moment from 'moment';
import React, { Fragment } from 'react';
import { useParams } from 'react-router';
import historyCutoffs from './historyCutoffs';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import { VersionHistoryDays } from '../../organization/PricingConfig';
import UpgradeButton from '../../organization/UpgradeButton';
import getFeature from '../../utils/features';
import groupByCutoffs from '../../utils/groupByCutoffs';
import nonEmpty from '../../utils/nonEmpty';
import { RIGHT_PANEL_SHIFT_BREAKPOINT } from '../../utils/responsive/Sizes';
import useNavigate from '../../utils/router/useNavigate';
import historyPath from '../../utils/url/historyPath';
import { openPricingModal } from '../../utils/url/pricingPath';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import HistoryRow from './HistoryRow';
import * as styles from './PostHistory.module.scss';
import { useRightPanel } from '../../app/layout/RightPanel';

// eslint-disable-next-line slab-graphql/no-unused-fields
const POST_HISTORY_QUERY = gql(/** @complexity 12 */ `
  query PostHistory($id: ID!) {
    post(id: $id) {
      id
      content(isLatest: true) {
        id
        delta
        version
      }
      ...historyPath_post
      ...usePostPolicyCheck_post
      histories {
        id
        insertedAt
        authors {
          id
          ...HistoryRow_authors
        }
        versionFrom
        versionTo
      }
    }
  }
`);

interface PostHistoryProps {
  postId: string;
}

const PostHistory = ({ postId }: PostHistoryProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();

  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );
  const currentUser = useAppSelector((state) => state.session.user);
  const { closeRightPanel } = useRightPanel();

  const { data, loading } = useQuery(POST_HISTORY_QUERY, {
    variables: { id: postId },
  });

  const post = data?.post;
  const postPolicyCheck = usePostPolicyCheck();

  if (!post || !planTier) {
    return null;
  }

  const restrictedAt = new Date(
    Date.now() - getFeature('version_history') * 24 * 3600 * 1000,
  );
  const restrictedHistories = post.histories.filter(
    (history) => new Date(history.insertedAt) > restrictedAt,
  );
  const isRestricted =
    !loading && restrictedHistories.length < post.histories.length;

  const upgradeClick = () => {
    openPricingModal(dispatch, 'versionHistory');
  };

  const rows = restrictedHistories.filter(
    ({ authors }) => authors && Object.keys(authors).length > 0,
  );

  const latestHistoryVersion = restrictedHistories[0]?.versionTo ?? 0;
  if (post.content.version > latestHistoryVersion) {
    rows.unshift({
      id: 'current',
      authors: [],
      insertedAt: moment().toISOString(),
      versionFrom: latestHistoryVersion,
      versionTo: post.content.version,
    });
  }
  const curFrom = params.versionFrom ? parseInt(params.versionFrom, 10) : 0;
  const curTo = params.versionTo ? parseInt(params.versionTo, 10) : 0;

  const groupedCutoff = groupByCutoffs(historyCutoffs(), rows, 'insertedAt');

  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);

  return (
    <>
      {groupedCutoff.length > 0 && (
        <div className={styles.sectionsContainer}>
          {groupedCutoff.map(({ items, key, label }) => (
            <Fragment key={key}>
              {label && <div className={styles.sectionHeader}>{label}</div>}
              {items.map(
                ({ id, authors, insertedAt, versionFrom, versionTo }) => {
                  const canRestore =
                    canEdit && versionTo !== post.content.version;
                  return (
                    <HistoryRow
                      key={id}
                      active={versionFrom >= curFrom && versionTo <= curTo}
                      authors={authors?.filter(nonEmpty) ?? []}
                      editedAt={insertedAt}
                      postId={postId}
                      versionFrom={versionFrom}
                      versionTo={versionTo}
                      isCurrent={id === 'current'}
                      canRestore={canRestore}
                      onClick={(e) => {
                        let targetFrom = versionFrom;
                        let targetTo = versionTo;
                        if (e.shiftKey) {
                          targetFrom = Math.min(versionFrom, curFrom);
                          targetTo = Math.max(versionTo, curTo);
                        }
                        navigate(historyPath(post, targetFrom, targetTo));
                        if (window.innerWidth < RIGHT_PANEL_SHIFT_BREAKPOINT) {
                          closeRightPanel();
                        }
                      }}
                    />
                  );
                },
              )}
            </Fragment>
          ))}
        </div>
      )}
      {isRestricted && (
        <div className={styles.restrictionContainer}>
          <h4>View more history</h4>
          <div className={styles.restrictionNote}>
            {VersionHistoryDays[planTier] === Infinity ? (
              <>Upgrade to review and restore older histories.</>
            ) : (
              <>
                Upgrade to review and restore histories older than{' '}
                {VersionHistoryDays[planTier]} days.
              </>
            )}
          </div>
          <UpgradeButton onClick={() => upgradeClick()}>
            View upgrade options
          </UpgradeButton>
        </div>
      )}
    </>
  );
};

export default PostHistory;
